import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { rulesApi, glossaryApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { rulePath, titleToSlug } from '../utils/slug';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import SEO from '../components/SEO';
import ShareRule from '../components/ShareRule';
import RuleCard from '../components/RuleCard';
import './RuleDetail.css';

function escapeRegex(str) {
  return String(str).replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
}

/** Split body by glossary terms (word boundary) and return array of strings and <mark> nodes for matches */
function highlightGlossaryTerms(body, terms) {
  if (typeof body !== 'string' || !terms?.length) return body || '';
  const sortedTerms = [...terms].sort((a, b) => (b.term?.length ?? 0) - (a.term?.length ?? 0));
  const pattern = sortedTerms
    .map((t) => t.term)
    .filter(Boolean)
    .map(escapeRegex)
    .join('|');
  if (!pattern) return body;
  const regex = new RegExp(`\\b(${pattern})\\b`, 'gi');
  const parts = body.split(regex);
  if (parts.length <= 1) return body;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <em key={i} className="glossary-term-in-body" title="Glossary term">
        {part}
      </em>
    ) : (
      part
    )
  );
}

function RuleDetail() {
  const { id, slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [rule, setRule] = useState(null);
  const [subs, setSubs] = useState([]);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votingClub, setVotingClub] = useState(false);

  const loadRule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rulesApi.getById(id);
      if (response.data.success) {
        setRule(response.data.data.rule || response.data.data);
        setSubs(response.data.data.subs || []);
      } else {
        setError(response.data.message || 'Failed to load rule');
      }
    } catch (err) {
      console.error('Error loading rule:', err);
      setError(err.response?.data?.message || 'Rule not found');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadGlossary = useCallback(async () => {
    if (!id) return;
    try {
      const response = await glossaryApi.getByRuleId(id);
      if (response.data.success && Array.isArray(response.data.data)) {
        setGlossaryTerms(response.data.data);
      } else {
        const data = response.data?.data;
        setGlossaryTerms(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Glossary load failed:', err);
      setGlossaryTerms([]);
    }
  }, [id]);

  useEffect(() => {
    loadRule();
  }, [id, loadRule]);

  useEffect(() => {
    loadGlossary();
  }, [id, loadGlossary]);

  // Scroll to top when a new rule has finished loading (e.g. after clicking a related sub-rule)
  useEffect(() => {
    if (!loading && rule) {
      window.scrollTo(0, 0);
    }
  }, [id, loading, rule]);

  // After rule loads, ensure URL includes slug for pretty links (replace so back button works)
  useEffect(() => {
    if (!rule || !id) return;
    const slug = rule.slug || titleToSlug(rule.gr_desc || rule.description || '');
    if (!slug) return;
    const expectedPath = `/rules/${id}/${slug}`;
    if (location.pathname !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }
  }, [rule, id, location.pathname, navigate]);

  const handleRandomRule = async () => {
    try {
      const response = await rulesApi.getRandom();
      const data = response.data.data.rule || response.data.data;
      const ruleId = data.id || data.gr_id;
      const slug = data.slug || titleToSlug(data.gr_desc || data.description || '');
      navigate(rulePath(ruleId, slug));
    } catch (err) {
      console.error('Failed to load random rule:', err);
    }
  };

  const handleClubVote = async (vote) => {
    if (!user) return;
    try {
      setVotingClub(true);
      await rulesApi.clubApproveVote(id, vote);
      await loadRule();
    } catch (err) {
      console.error('Club vote failed:', err);
    } finally {
      setVotingClub(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="container rule-detail-page">
          <Loading message="Loading rule…" />
        </div>
        <Footer />
      </>
    );
  }

  if (error || !rule) {
    return (
      <>
        <Header />
        <div className="container rule-detail-page">
          <div className="error-message">{error || 'Rule not found'}</div>
          <button type="button" className="btn btn-primary mt-3" onClick={() => loadRule()}>
            Try again
          </button>
        </div>
        <Footer />
      </>
    );
  }

  // Format rule number as number.subrule (e.g., 65.1, 99.4, 99.0)
  const formatRuleNumber = (rule) => {
    if (rule.formatted_number) return rule.formatted_number;
    if (rule.number) return rule.number;
    if (rule.gr_num !== undefined) {
      const num = rule.gr_num ?? 0;
      const sub = rule.gr_sub ?? 0;
      return `${num}.${sub}`;
    }
    return '0.0';
  };

  const ruleNumber = formatRuleNumber(rule);
  const description = rule.gr_desc || rule.description;
  const body = rule.gr_body || rule.body;
  const category = rule.gr_other1 || rule.category_name;
  const bodyText = typeof body === 'string' ? body.replace(/<[^>]*>/g, '').trim() : '';
  const metaDesc = bodyText.length > 0
    ? (bodyText.slice(0, 155) + (bodyText.length > 155 ? '...' : ''))
    : `Guy Rule: ${description}`;

  const ruleUrl = typeof window !== 'undefined' ? `${window.location.origin}${rulePath(id, rule.slug || titleToSlug(description))}` : '';
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: description,
    description: metaDesc,
    url: ruleUrl,
    publisher: { '@type': 'Organization', name: 'GuyRules' },
  };

  return (
    <>
      <SEO
        title={`Guy Rule #${ruleNumber}: ${description}`}
        description={metaDesc}
        canonicalPath={rulePath(id, rule.slug || titleToSlug(description))}
        jsonLd={articleJsonLd}
      />
      <Header />
      <div className="container rule-detail-page">
        <div className="rule-actions">
          <button onClick={handleRandomRule} className="btn-random">
            Random Rule
          </button>
          <button onClick={() => navigate('/rules')} className="btn-back">
            Back to Rules
          </button>
        </div>

        <article className="rule-content">
          <header className="rule-header">
            <h1>{description}</h1>
            <div className="rule-meta">
              <span>Rule #{ruleNumber}</span>
              {category && <span>{category}</span>}
              {rule.rating && (
                <span>⭐ {parseFloat(rule.rating).toFixed(1)}</span>
              )}
            </div>
          </header>

          <div className="rule-body">
            <p>{glossaryTerms.length > 0 ? highlightGlossaryTerms(body, glossaryTerms) : body}</p>
          </div>

          {glossaryTerms.length > 0 && (
            <section className="rule-glossary">
              <h2>Glossary</h2>
              <div className="glossary-terms">
                {glossaryTerms.map((item) => (
                  <div key={item.term_id || item.id || item.term} className="glossary-term">
                    <div className="glossary-term-name">{item.term}</div>
                    <div className="glossary-term-def">{item.def ?? item.definition ?? ''}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rule-club-approve">
            <h3>Club approval</h3>
            <div className="club-approve-counts">
              <span className="club-approve-agree">Agree: {rule.club_agree_count ?? 0}</span>
              <span className="club-approve-disagree">Disagree: {rule.club_disagree_count ?? 0}</span>
            </div>
            {user && (
              <>
                {(rule.my_club_vote === 1 || rule.my_club_vote === '1') && (
                  <div className="club-approve-result club-approve-result-agree">
                    <p className="club-approve-result-line">
                      <i className="bi bi-hand-thumbs-up me-1" aria-hidden="true" />
                      You agreed with this rule.
                    </p>
                    <p className="club-approve-result-change">but you can change your vote below</p>
                  </div>
                )}
                {(rule.my_club_vote === 0 || rule.my_club_vote === '0') && (
                  <div className="club-approve-result club-approve-result-disagree">
                    <p className="club-approve-result-line">
                      <i className="bi bi-hand-thumbs-down me-1" aria-hidden="true" />
                      You disagreed with this rule.
                    </p>
                    <p className="club-approve-result-change">but you can change your vote below</p>
                  </div>
                )}
                <div className="club-approve-actions">
                  <button
                    type="button"
                    className={`btn btn-sm ${(rule.my_club_vote === 1 || rule.my_club_vote === '1') ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => handleClubVote(1)}
                    disabled={votingClub}
                  >
                    <i className="bi bi-hand-thumbs-up me-1" aria-hidden="true" /> Agree
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${(rule.my_club_vote === 0 || rule.my_club_vote === '0') ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => handleClubVote(0)}
                    disabled={votingClub}
                  >
                    <i className="bi bi-hand-thumbs-down me-1" aria-hidden="true" /> Disagree
                  </button>
                  {votingClub && <span className="ms-2">Saving…</span>}
                </div>
              </>
            )}
            {!user && (
              <p className="club-approve-login">Log in to record your Agree or Disagree.</p>
            )}
          </section>

          <ShareRule
            url={typeof window !== 'undefined' ? `${window.location.origin}/rules/${id}` : undefined}
            title={description}
            ruleNumber={ruleNumber}
          />

          {subs.length > 0 && (
            <section className="rule-subs" aria-labelledby="related-rules-heading">
              <h2 id="related-rules-heading" className="rule-subs-title">Related Rules</h2>
              <div className="rule-subs-grid">
                {subs.map((sub) => (
                  <RuleCard key={sub.gr_id || sub.id} rule={sub} />
                ))}
              </div>
            </section>
          )}

          <footer className="rule-footer">
            {rule.name && (
              <p>
                Submitted by: {rule.name} on {rule.date}
              </p>
            )}
          </footer>
        </article>
      </div>
      <Footer />
    </>
  );
}

export default RuleDetail;
